/**
 * Visitor source attribution (client side).
 *
 * Answers "where did this visitor come from?" for the admin visitor log:
 * a search engine, PeerPush, a newsletter link, someone's referral link, etc.
 *
 * Captured once per page load, at module import time (see main.tsx), so it
 * runs before anything else has a chance to mint the visitor ID. That ordering
 * matters: it is how we know whether this browser is brand new.
 *
 *  - First touch: the source of the very first visit from this browser.
 *    Stored in localStorage and never changed. Only recorded for browsers that
 *    are genuinely new right now, so a visitor from before this shipped is
 *    never mislabelled with the source of a later visit.
 *  - Latest touch: the most recent visit that arrived from somewhere (not a
 *    direct visit or a reload). Lets the admin see a returning visitor who
 *    came back through, say, a referral link.
 *
 * Consent: nothing is stored or sent until the visitor has made an explicit
 * choice that allows analytics (the cookie banner or Cookie preferences), in
 * every region. Until then the touch is held in memory for this page load only;
 * if they allow analytics during the visit it is stored and sent then. An
 * explicit refusal clears any stored first touch.
 *
 * Privacy: only the referring site's domain is kept (never the full referring
 * URL, which can carry search terms or personal paths), plus the campaign tags
 * and referral code in our own links. No IP, no fingerprint.
 */

import { getConsentState, isAnalyticsGranted, subscribeConsent } from './consent/consentManager';

const VISITOR_KEY = 'meinvent.visitorId'; // must match visitorId.ts
const FIRST_KEY = 'entredash.firstTouch';

export interface Touch {
  /** Referring site's domain, e.g. `google.com`, `peerpush.net`. */
  referrerDomain?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  /** Referral code from a `?ref=` link. */
  refCode?: string;
  /** Which ad network's click id was present (`gclid`, `fbclid`, …), not its value. */
  clickId?: string;
  /** The path the visitor landed on. */
  landingPath?: string;
  /** Unix ms when this touch was captured. */
  at: number;
}

/** Domains that send people back to us mid-journey, not real sources. */
const IGNORED_REFERRERS = [
  'stripe.com',
  'checkout.stripe.com',
  'accounts.google.com',
  'mindstudio.ai',
  'madewithremy.com',
  'msagent.ai',
  'localhost',
];

const CLICK_IDS = ['gclid', 'fbclid', 'msclkid', 'rdt_cid', 'li_fat_id', 'ttclid', 'twclid'];

/** Short, safe tag value from a URL param. */
function tag(v: string | null, max = 80): string | undefined {
  if (!v) return undefined;
  const s = v.trim().slice(0, max);
  return s || undefined;
}

function referrerDomain(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    const host = new URL(document.referrer).hostname.toLowerCase().replace(/^www\./, '');
    if (!host || host === window.location.hostname.replace(/^www\./, '')) return undefined;
    if (IGNORED_REFERRERS.some((d) => host === d || host.endsWith(`.${d}`))) return undefined;
    return host;
  } catch {
    return undefined;
  }
}

/** Read this page load's source. Null when it's a direct visit or a reload. */
function readTouch(): Touch | null {
  try {
    const p = new URLSearchParams(window.location.search);
    const touch: Touch = {
      referrerDomain: referrerDomain(),
      utmSource: tag(p.get('utm_source')),
      utmMedium: tag(p.get('utm_medium')),
      utmCampaign: tag(p.get('utm_campaign')),
      refCode: tag(p.get('ref'), 16)?.toUpperCase(),
      clickId: CLICK_IDS.find((k) => p.has(k)),
      landingPath: window.location.pathname.slice(0, 120),
      at: Date.now(),
    };
    const hasSource =
      touch.referrerDomain || touch.utmSource || touch.utmMedium || touch.utmCampaign || touch.refCode || touch.clickId;
    return hasSource ? touch : null;
  } catch {
    return null;
  }
}

let firstTouch: Touch | null = null;
let latestTouch: Touch | null = null;
let isNewBrowser = false;

/** Allowed only by an explicit choice that grants analytics, never by a default. */
function allowed(): boolean {
  return getConsentState().decided && isAnalyticsGranted();
}

/** Store a new browser's first touch, once allowed. */
function persistFirst(): void {
  try {
    if (!firstTouch || !isNewBrowser || !allowed()) return;
    if (!window.localStorage.getItem(FIRST_KEY)) window.localStorage.setItem(FIRST_KEY, JSON.stringify(firstTouch));
  } catch {
    /* best effort */
  }
}

/**
 * Capture this page load's source. Call once, before the visitor ID is
 * created anywhere (main.tsx does it at import time).
 */
export function captureAttribution(): void {
  try {
    isNewBrowser = !window.localStorage.getItem(VISITOR_KEY);
    latestTouch = readTouch();

    const stored = window.localStorage.getItem(FIRST_KEY);
    if (stored) {
      firstTouch = JSON.parse(stored) as Touch;
    } else if (isNewBrowser) {
      // A brand-new browser: this visit is its first touch, even when it's a
      // direct visit (recorded with just the landing path, so "direct" is a
      // real answer rather than "unknown"). Held in memory until allowed.
      firstTouch = latestTouch ?? { landingPath: window.location.pathname.slice(0, 120), at: Date.now() };
    }
    persistFirst();
    // A choice made during this visit: store on allow, clear on an explicit refusal.
    subscribeConsent(() => {
      const st = getConsentState();
      if (allowed()) persistFirst();
      else if (st.decided && st.analytics === 'denied') {
        try {
          window.localStorage.removeItem(FIRST_KEY);
        } catch {
          /* best effort */
        }
      }
    });
  } catch {
    // Best effort. Attribution must never break a page load.
  }
}

let sentThisLoad = false;

/**
 * The touches to send with the next heartbeat. Returned only once per page
 * load, so the 60-second heartbeat doesn't resend them every minute.
 */
export function takeTouchesForHeartbeat(): { firstTouch?: Touch; latestTouch?: Touch } {
  // Not sent at all without an explicit analytics choice; tried again on a later heartbeat if one is made.
  if (sentThisLoad || !allowed()) return {};
  sentThisLoad = true;
  return {
    ...(firstTouch ? { firstTouch } : {}),
    ...(latestTouch ? { latestTouch } : {}),
  };
}
